import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, DollarSign } from 'lucide-react';
import { useClinicConfigContext } from '@/contexts/ClinicConfigContext';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Footer } from '@/components/Footer';
import { InfoCard } from '@/components/InfoCard';
import { TabNavigation, Tab } from '@/components/TabNavigation';
import { InsuranceBenefitsTable } from '@/components/InsuranceBenefitsTable';
import { CDTCodesTable } from '@/components/CDTCodesTable';

// Animation variants
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

// Mock data structure matching the reference
const mockInsuranceData = {
  patient: {
    name: 'John Smith',
    memberId: '120177822001',
  },
  payer: {
    name: 'Delta Dental',
    groupNumber: 'GRP-5467891',
    planEffectiveDate: '2024-01-01',
  },
  ppoBenefitsSummary: {
    annualMaximum: { individual: '2500', family: '4500' },
    deductible: { individual: '250', family: '500' },
    remainingMaximum: { individual: '1800', family: '3200' },
    remainingDeductible: { individual: '150', family: '350' },
  },
  networkBenefits: {
    ppo: [
      { serviceCategory: 'Preventive Care', planPays: '100%', limitations: 'Twice per calendar year' },
      { serviceCategory: 'Basic Restorative', planPays: '80%', limitations: 'After deductible' },
      { serviceCategory: 'Major Restorative', planPays: '50%', limitations: 'After deductible, 6-month waiting period' },
      { serviceCategory: 'Orthodontics', planPays: '50%', limitations: 'Lifetime maximum $1,500' },
    ],
    preferred: [
      { serviceCategory: 'Preventive Care', planPays: '100%', limitations: 'Twice per calendar year' },
      { serviceCategory: 'Basic Restorative', planPays: '85%', limitations: 'After deductible' },
      { serviceCategory: 'Major Restorative', planPays: '60%', limitations: 'After deductible, 6-month waiting period' },
      { serviceCategory: 'Orthodontics', planPays: '60%', limitations: 'Lifetime maximum $2,000' },
    ],
    premier: [
      { serviceCategory: 'Preventive Care', planPays: '100%', limitations: 'Twice per calendar year' },
      { serviceCategory: 'Basic Restorative', planPays: '90%', limitations: 'After deductible' },
      { serviceCategory: 'Major Restorative', planPays: '70%', limitations: 'After deductible, 6-month waiting period' },
      { serviceCategory: 'Orthodontics', planPays: '70%', limitations: 'Lifetime maximum $2,500' },
    ],
    outOfNetwork: [
      { serviceCategory: 'Preventive Care', planPays: '80%', limitations: 'Twice per calendar year' },
      { serviceCategory: 'Basic Restorative', planPays: '60%', limitations: 'After deductible' },
      { serviceCategory: 'Major Restorative', planPays: '40%', limitations: 'After deductible, 6-month waiting period' },
      { serviceCategory: 'Orthodontics', planPays: '40%', limitations: 'Lifetime maximum $1,000' },
    ],
  },
  planCategories: {
    preventive: [
      { id: 1, code: 'D0120', description: 'Periodic oral evaluation', coverage_percentage: 100 },
      { id: 2, code: 'D0150', description: 'Comprehensive oral evaluation', coverage_percentage: 100 },
      { id: 3, code: 'D1110', description: 'Prophylaxis - adult', coverage_percentage: 100 },
      { id: 4, code: 'D1120', description: 'Prophylaxis - child', coverage_percentage: 100 },
    ],
    basic: [
      { id: 5, code: 'D2140', description: 'Amalgam - one surface', coverage_percentage: 80 },
      { id: 6, code: 'D2150', description: 'Amalgam - two surfaces', coverage_percentage: 80 },
      { id: 7, code: 'D2330', description: 'Resin-based composite - one surface', coverage_percentage: 80 },
      { id: 8, code: 'D2740', description: 'Crown - porcelain/ceramic', coverage_percentage: 80 },
    ],
    major: [
      { id: 9, code: 'D2750', description: 'Crown - porcelain fused to metal', coverage_percentage: 50 },
      { id: 10, code: 'D2790', description: 'Crown - full cast metal', coverage_percentage: 50 },
      { id: 11, code: 'D6010', description: 'Surgical placement of implant', coverage_percentage: 50 },
      { id: 12, code: 'D6058', description: 'Abutment supported porcelain/ceramic crown', coverage_percentage: 50 },
    ],
  },
};

export function InsuranceDetailsPage() {
  const { clinicSlug } = useParams<{ clinicSlug: string }>();
  const navigate = useNavigate();
  const { clinic, clinicLoading, clinicError } = useClinicConfigContext();

  const [activeNetworkTab, setActiveNetworkTab] = useState<string>('ppo');
  const [activeCategoryTab, setActiveCategoryTab] = useState<string>('preventive');

  const networkTabs: Tab[] = [
    { id: 'ppo', label: 'PPO Network' },
    { id: 'preferred', label: 'Preferred Network' },
    { id: 'premier', label: 'Premier Network' },
    { id: 'outOfNetwork', label: 'Out-of-Network' },
  ];

  const categoryTabs: Tab[] = [
    { id: 'preventive', label: 'Preventive' },
    { id: 'basic', label: 'Basic' },
    { id: 'major', label: 'Major' },
  ];

  const getNetworkBenefitsData = () => {
    switch (activeNetworkTab) {
      case 'ppo':
        return mockInsuranceData.networkBenefits.ppo;
      case 'preferred':
        return mockInsuranceData.networkBenefits.preferred;
      case 'premier':
        return mockInsuranceData.networkBenefits.premier;
      case 'outOfNetwork':
        return mockInsuranceData.networkBenefits.outOfNetwork;
      default:
        return mockInsuranceData.networkBenefits.ppo;
    }
  };

  const getCDTCodesData = () => {
    switch (activeCategoryTab) {
      case 'preventive':
        return mockInsuranceData.planCategories.preventive;
      case 'basic':
        return mockInsuranceData.planCategories.basic;
      case 'major':
        return mockInsuranceData.planCategories.major;
      default:
        return mockInsuranceData.planCategories.preventive;
    }
  };

  if (clinicLoading) {
    return (
      <div className='min-h-screen relative'>
        <LoadingSpinner fullScreen size='lg' text='Loading...' />
      </div>
    );
  }

  if (clinicError || !clinic) {
    return (
      <div className='min-h-screen relative flex items-center justify-center'>
        <div className='text-center'>
          <h1 className='text-4xl font-bold text-gray-800 mb-4'>404</h1>
          <p className='text-xl text-gray-600 mb-8'>Clinic not found</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className='h-screen w-screen overflow-hidden relative flex flex-col'
      variants={pageVariants}
      initial='initial'
      animate='animate'
      exit='exit'
      style={{ backgroundColor: 'var(--clinic-primary)' }}
    >
      {/* Header with Logo */}
      <motion.div className='flex-shrink-0 p-6' variants={fadeInUp}>
        <div className='flex items-center'>
          <div className='w-fit'>
            <img
              src={'/logo.png'}
              alt={`${clinic.name} logo`}
              className='w-full h-full object-contain'
            />
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className='flex-1 overflow-y-auto px-6 py-8'>
        <div className='max-w-6xl mx-auto w-full'>
          {/* Page Title */}
          <motion.div className='text-left mb-8' variants={fadeInUp}>
            <h1
              className='text-3xl lg:text-5xl font-semibold'
              style={{ color: 'var(--clinic-text)' }}
            >
              Your Insurance Coverage
            </h1>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial='initial'
            animate='animate'
            className='space-y-8'
          >
            {/* Insurance Card Header */}
            <motion.div
              variants={fadeInUp}
              className='rounded-xl p-6 shadow-sm'
              style={{ backgroundColor: 'white' }}
            >
              <div className='flex justify-between items-start flex-wrap gap-4'>
                {/* Patient Info - Left Side */}
                <div className='flex flex-col'>
                  <h3 className='text-xl font-semibold text-gray-800 mb-1'>
                    {mockInsuranceData.patient.name}
                  </h3>
                  <span className='text-xs font-semibold text-gray-900'>
                    <span className='font-normal'>Member ID:</span>{' '}
                    {mockInsuranceData.patient.memberId}
                  </span>
                </div>

                {/* Insurance Info - Right Side */}
                <div className='flex flex-col items-end text-right'>
                  <h4 className='text-xl font-semibold mb-1' style={{ color: 'var(--clinic-primary)' }}>
                    {mockInsuranceData.payer.name}
                  </h4>
                  <div className='text-xs font-normal text-gray-900'>
                    <div>
                      Group:{' '}
                      <span className='font-semibold'>
                        {mockInsuranceData.payer.groupNumber}
                      </span>
                    </div>
                    <div>
                      Plan Effective:{' '}
                      <span className='font-semibold'>
                        {new Date(
                          mockInsuranceData.payer.planEffectiveDate
                        ).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Info Cards Grid */}
            <motion.div
              variants={fadeInUp}
              className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'
            >
              <InfoCard
                icon={Home}
                title='Annual Maximum (PPO)'
                value={`$${mockInsuranceData.ppoBenefitsSummary.annualMaximum.individual} / $${mockInsuranceData.ppoBenefitsSummary.annualMaximum.family}`}
                borderColor='#7A5AF8'
                iconBackgroundColor='#F3F0FF'
                iconColor='#7A5AF8'
              />
              <InfoCard
                icon={DollarSign}
                title='Deductible (Ind/Fam, PPO)'
                value={`$${mockInsuranceData.ppoBenefitsSummary.deductible.individual} / $${mockInsuranceData.ppoBenefitsSummary.deductible.family}`}
                borderColor='#4EC5A8'
                iconBackgroundColor='#E8F8F5'
                iconColor='#4EC5A8'
              />
              <InfoCard
                icon={DollarSign}
                title='Remaining Max (PPO)'
                value={`$${mockInsuranceData.ppoBenefitsSummary.remainingMaximum.individual} / $${mockInsuranceData.ppoBenefitsSummary.remainingMaximum.family}`}
                borderColor='#F79009'
                iconBackgroundColor='#FEF0C7'
                iconColor='#F79009'
              />
              <InfoCard
                icon={DollarSign}
                title='Remaining Deductible'
                value={`$${mockInsuranceData.ppoBenefitsSummary.remainingDeductible.individual} / $${mockInsuranceData.ppoBenefitsSummary.remainingDeductible.family}`}
                borderColor='#F04438'
                iconBackgroundColor='#FEE4E2'
                iconColor='#F04438'
              />
            </motion.div>

            {/* Network Benefits Section */}
            <motion.div variants={fadeInUp} className='space-y-4'>
              <h2
                className='text-2xl font-semibold'
                style={{ color: 'var(--clinic-text)' }}
              >
                Network Benefits
              </h2>

              <TabNavigation
                tabs={networkTabs}
                activeTab={activeNetworkTab}
                onTabChange={setActiveNetworkTab}
              />

              <AnimatePresence mode='wait'>
                <motion.div
                  key={activeNetworkTab}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{
                    type: 'tween',
                    ease: 'anticipate',
                    duration: 0.3,
                  }}
                >
                  <InsuranceBenefitsTable data={getNetworkBenefitsData()} />
                </motion.div>
              </AnimatePresence>
            </motion.div>

            {/* CDT Code Coverage Section */}
            <motion.div variants={fadeInUp} className='space-y-4'>
              <h2
                className='text-2xl font-semibold'
                style={{ color: 'var(--clinic-text)' }}
              >
                CDT Code Coverage by Plan Category
              </h2>

              <TabNavigation
                tabs={categoryTabs}
                activeTab={activeCategoryTab}
                onTabChange={setActiveCategoryTab}
              />

              <AnimatePresence mode='wait'>
                <motion.div
                  key={activeCategoryTab}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{
                    type: 'tween',
                    ease: 'anticipate',
                    duration: 0.3,
                  }}
                >
                  <CDTCodesTable data={getCDTCodesData()} />
                </motion.div>
              </AnimatePresence>
            </motion.div>

            {/* Continue Button */}
            <motion.div variants={fadeInUp} className='flex justify-start pb-8'>
              <motion.button
                onClick={() => navigate(`/${clinicSlug}/appointment-confirmation`)}
                className='h-[54px] min-w-[300px] px-6 py-3 rounded-xl text-base transition-all duration-300 flex items-center justify-center gap-2.5 font-semibold hover:opacity-90'
                style={{
                  backgroundColor: 'var(--clinic-secondary)',
                  color: 'var(--clinic-text)',
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Continue to Confirmation
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <motion.div variants={fadeInUp}>
        <Footer mobileColumnLayout={true} />
      </motion.div>
    </motion.div>
  );
}

export default InsuranceDetailsPage;

